import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';
import { bookingStudioOf, studioIdOf } from '../utils/studioOf';

/**
 * The refund queue.
 *
 * Deliberately NOT its own route: it is a view of the Bookings page
 * (`/bookings?view=refunds`), because a refund is only ever a fact about a
 * cancelled booking and a separate left-nav entry would imply otherwise.
 *
 * Every row here has already passed the studio's cancellation window —
 * a cancellation that missed it is recorded SKIPPED and can only be read, not
 * approved, so nobody can pay out a refund the policy says is not owed.
 */

type RefundRow = {
  _id: string;
  status: 'REQUESTED' | 'INITIATED' | 'PROCESSED' | 'FAILED' | 'REJECTED' | 'SKIPPED';
  amount: number;
  mode: 'auto' | 'manual' | 'none';
  gateway: 'ss' | 'studio';
  reason?: string | null;
  failureReason?: string | null;
  createdAt?: string;
  cancelledAt?: string;
  razorpayRefundId?: string | null;
  user?: { name?: string; email?: string; phoneNumber?: string } | null;
  booking?: { bookingId?: string; cancelledAt?: string; studio?: any; session?: any; event?: any } | null;
  studio?: { _id?: string; studioName?: string } | null;
};

/** The location a refund belongs to: its own `studio`, else its booking's. */
const refundStudioOf = (row: RefundRow): string | null =>
  studioIdOf(row.studio) || bookingStudioOf(row.booking);

const STATUS_STYLE: Record<string, { label: string; color: string }> = {
  REQUESTED: { label: 'Requested', color: colors.warning },
  INITIATED: { label: 'Processing', color: colors.info },
  PROCESSED: { label: 'Refunded', color: colors.success },
  FAILED: { label: 'Failed', color: colors.error },
  REJECTED: { label: 'Declined', color: colors.textSecondary },
  SKIPPED: { label: 'No refund', color: colors.textSecondary },
};

const FILTERS: { value: string; label: string }[] = [
  { value: 'REQUESTED', label: 'Needs action' },
  { value: 'INITIATED', label: 'Processing' },
  { value: 'PROCESSED', label: 'Refunded' },
  { value: 'FAILED', label: 'Failed' },
  { value: '', label: 'All' },
];

interface Props {
  /** Super admins may scope the queue to one studio, matching the list view. */
  studioId?: string;
  isSuperAdmin: boolean;
}

const RefundsPanel: React.FC<Props> = ({ studioId, isSuperAdmin }) => {
  const [rows, setRows] = useState<RefundRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('REQUESTED');
  const [pendingCount, setPendingCount] = useState(0);
  const [rejectTarget, setRejectTarget] = useState<RefundRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchRefunds = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await apiService.getRefunds({
        status: status || undefined,
        studioId: studioId || undefined,
        limit: 100,
      });
      setRows(res?.data ?? []);
      setPendingCount(res?.pendingCount ?? 0);
      setError(null);
    } catch (err: any) {
      setError(err?.data?.message || err.message || 'Failed to load refunds');
    } finally {
      setLoading(false);
    }
  }, [status, studioId]);

  useEffect(() => {
    fetchRefunds();
  }, [fetchRefunds]);

  const process = async (row: RefundRow) => {
    setBusyId(row._id);
    try {
      // Pinned to the refund's own location — "All locations" refuses writes.
      await apiService.processRefund(row._id, refundStudioOf(row));
      setError(null);
      await fetchRefunds();
    } catch (err: any) {
      setError(err?.data?.message || err.message || 'Failed to process refund');
    } finally {
      setBusyId(null);
    }
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setBusyId(rejectTarget._id);
    try {
      await apiService.rejectRefund(
        rejectTarget._id,
        rejectReason.trim() || undefined,
        refundStudioOf(rejectTarget),
      );
      setRejectTarget(null);
      setRejectReason('');
      setError(null);
      await fetchRefunds();
    } catch (err: any) {
      setError(err?.data?.message || err.message || 'Failed to decline refund');
    } finally {
      setBusyId(null);
    }
  };

  const money = (n?: number) => `₹${(n ?? 0).toLocaleString('en-IN')}`;
  const when = (d?: string | null) => (d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

  return (
    <Card sx={{ border: `1px solid ${colors.divider}` }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Refunds
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pendingCount > 0
                ? `${pendingCount} refund${pendingCount === 1 ? '' : 's'} waiting for approval`
                : 'Nothing waiting for approval'}
            </Typography>
          </Box>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={status}
            onChange={(_, next) => next !== null && setStatus(next)}
          >
            {FILTERS.map((f) => (
              <ToggleButton key={f.value || 'all'} value={f.value} sx={{ textTransform: 'none', px: 2 }}>
                {f.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box py={6} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              No refunds in this state.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Member</TableCell>
                  <TableCell>Booking</TableCell>
                  {isSuperAdmin && <TableCell>Studio</TableCell>}
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Cancelled</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const style = STATUS_STYLE[r.status];
                  const actionable = r.status === 'REQUESTED' || r.status === 'FAILED';
                  return (
                    <TableRow key={r._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {r.user?.name || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {r.user?.phoneNumber || r.user?.email || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{r.booking?.bookingId || '—'}</Typography>
                      </TableCell>
                      {isSuperAdmin && (
                        <TableCell>
                          <Typography variant="caption">{r.studio?.studioName || '—'}</Typography>
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700}>
                          {money(r.amount)}
                        </Typography>
                        {/* Whose account the money leaves. A studio-gateway
                            refund never touches a SweatSocial payout. */}
                        <Typography variant="caption" color="text.secondary">
                          {r.gateway === 'ss' ? 'SweatSocial a/c' : 'Studio a/c'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={r.failureReason || r.reason || r.razorpayRefundId || ''}>
                          <Chip
                            label={style?.label ?? r.status}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              backgroundColor: `${style?.color ?? colors.textSecondary}20`,
                              color: style?.color ?? colors.textSecondary,
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{when(r.cancelledAt || r.booking?.cancelledAt)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        {actionable ? (
                          <Box display="flex" gap={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              variant="contained"
                              disabled={busyId === r._id}
                              onClick={() => process(r)}
                            >
                              {r.status === 'FAILED' ? 'Retry' : 'Approve'}
                            </Button>
                            {r.status === 'REQUESTED' && (
                              <Button
                                size="small"
                                color="inherit"
                                disabled={busyId === r._id}
                                onClick={() => {
                                  setRejectTarget(r);
                                  setRejectReason('');
                                }}
                              >
                                Decline
                              </Button>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent>

      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Decline refund</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            No money will be sent. The decision is recorded against booking{' '}
            {rejectTarget?.booking?.bookingId ?? ''}.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button color="error" onClick={confirmReject} disabled={!!busyId}>
            Decline refund
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default RefundsPanel;
